"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";

/**
 * Tema claro/escuro com ESTRATEGIA DE CLASSE.
 *
 * O Tailwind v4 deste projeto declara `@custom-variant dark (&:where(.dark, .dark *))`
 * em globals.css: quem manda e a classe `dark` no <html>, nao a media query do
 * sistema. Logo, este provider tem uma unica responsabilidade real — manter
 * `document.documentElement.classList` em dia — e o estado React existe so para
 * os componentes saberem qual icone desenhar (lua ou sol).
 */

export type Theme = "light" | "dark";

/**
 * Chave do localStorage. A presenca dela significa "o usuario escolheu"; a
 * ausencia significa "siga o sistema operacional". Essa distincao e o que
 * permite acompanhar o SO em tempo real sem atropelar uma escolha manual.
 */
export const THEME_STORAGE_KEY = "valbot-theme";

interface ThemeContextValue {
  theme: Theme;
  setTheme: (t: Theme) => void;
  toggleTheme: () => void;
  /** false durante o SSR e ate o primeiro efeito rodar. */
  mounted: boolean;
}

// Default nulo de proposito: um objeto "neutro" faria um componente fora do
// provider funcionar pela metade (tema sempre claro, toggle que nao faz nada) e
// o bug so apareceria em producao. Nulo + throw quebra alto e cedo.
const ThemeContext = createContext<ThemeContextValue | null>(null);

/** Le a escolha manual gravada. Devolve null quando nao ha escolha. */
function lerEscolha(): Theme | null {
  try {
    const valor = window.localStorage.getItem(THEME_STORAGE_KEY);
    return valor === "light" || valor === "dark" ? valor : null;
  } catch {
    // localStorage lanca em modo privado/cookies bloqueados. Sem escolha
    // gravada, cair no sistema operacional e o comportamento correto.
    return null;
  }
}

function gravarEscolha(theme: Theme): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Sem persistencia o tema vale so para esta aba — degrade aceitavel,
    // preferivel a derrubar a UI por causa de uma preferencia visual.
  }
}

function aplicarNoDocumento(theme: Theme): void {
  const raiz = document.documentElement;
  raiz.classList.toggle("dark", theme === "dark");
  // colorScheme nao e redundante com a classe: e ele que pinta as partes que o
  // CSS da app nao alcanca — barra de rolagem, <input type="date">, autofill e
  // o fundo do proprio viewport durante a navegacao.
  raiz.style.colorScheme = theme;
}

export function ThemeProvider({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  // "light" fixo no primeiro render, tanto no servidor quanto no cliente: ler
  // localStorage/matchMedia durante o render tornaria o HTML do servidor
  // diferente do primeiro render do cliente e quebraria a hidratacao. O visual
  // correto ja foi aplicado antes da pintura pelo <ThemeScript />; este estado
  // so alcanca o React um instante depois.
  const [theme, setThemeState] = useState<Theme>("light");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const consultaSO = window.matchMedia("(prefers-color-scheme: dark)");
    const escolha = lerEscolha();
    const inicial: Theme = escolha ?? (consultaSO.matches ? "dark" : "light");

    aplicarNoDocumento(inicial);
    setThemeState(inicial);
    setMounted(true);

    const aoMudarSO = (evento: MediaQueryListEvent) => {
      // So acompanha o SO enquanto NAO houver escolha manual. A releitura e
      // feita aqui, e nao capturada em cima, porque o usuario pode ter clicado
      // na lua entre a montagem e este evento.
      if (lerEscolha() !== null) return;
      const doSO: Theme = evento.matches ? "dark" : "light";
      aplicarNoDocumento(doSO);
      setThemeState(doSO);
    };

    consultaSO.addEventListener("change", aoMudarSO);
    return () => consultaSO.removeEventListener("change", aoMudarSO);
  }, []);

  const setTheme = useCallback((proximo: Theme) => {
    gravarEscolha(proximo);
    // Mexe no DOM AGORA, sem esperar um efeito: o clique na lua precisa pintar
    // no mesmo frame. Deixar isso para um useEffect([theme]) insere um render
    // inteiro entre o clique e a troca de cor, e da para ver o atraso.
    aplicarNoDocumento(proximo);
    setThemeState(proximo);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === "dark" ? "light" : "dark");
  }, [setTheme, theme]);

  const valor = useMemo<ThemeContextValue>(
    () => ({ theme, setTheme, toggleTheme, mounted }),
    [theme, setTheme, toggleTheme, mounted],
  );

  return <ThemeContext value={valor}>{children}</ThemeContext>;
}

export function useTheme(): ThemeContextValue {
  const contexto = useContext(ThemeContext);
  if (contexto === null) {
    throw new Error(
      "useTheme() foi chamado fora do <ThemeProvider>. Envolva a arvore em " +
        "src/app/layout.tsx.",
    );
  }
  return contexto;
}

/*
 * A chave aparece literal dentro do script porque o conteudo e injetado como
 * texto bruto no HTML: interpolar a constante no template exigiria confiar no
 * escape do valor. Se THEME_STORAGE_KEY mudar, mude aqui tambem.
 */
const SCRIPT_ANTIFLASH = `(function(){try{var e=document.documentElement,v=localStorage.getItem("valbot-theme"),d=v==="dark"||(v!=="light"&&window.matchMedia("(prefers-color-scheme: dark)").matches);e.classList.toggle("dark",d);e.style.colorScheme=d?"dark":"light"}catch(e){}})();`;

/**
 * Script sincrono que decide o tema ANTES da primeira pintura.
 *
 * Sem ele o servidor sempre entrega HTML claro e o React so aplica a classe
 * `dark` depois de hidratar: quem usa tema escuro leva um flash branco na cara a
 * cada navegacao dura. Por isso e um <script> inline no <head>, e nao um efeito.
 *
 * O try/catch envolve TUDO por um motivo concreto: localStorage LANCA em modo
 * privado de alguns browsers e quando cookies de terceiros estao bloqueados.
 * Uma excecao aqui aborta o script inteiro antes do classList.toggle — e como
 * este script roda no <head>, o usuario ficaria com a pagina em estado
 * inconsistente. Falhar em silencio no tema claro e o pior caso aceitavel.
 */
export function ThemeScript(): ReactElement {
  return (
    <script
      // suppressHydrationWarning: o script muda o <html> antes de o React
      // hidratar, e o proprio no do script e comparado na hidratacao.
      suppressHydrationWarning
      dangerouslySetInnerHTML={{ __html: SCRIPT_ANTIFLASH }}
    />
  );
}
